import { AddressAutocomplete } from '@/components/blocks/inputs/AddressAutocomplete';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { CountryWithSubdivisionField } from './CountryWithSubdivisionField';
import { TextField } from './TextField';
import { useStreetAutocomplete } from '@/hooks/useStreetAutocomplete';
import type { ResolvedAddress } from '@/lib/parseAddressResponse';
import { type VariantProps } from 'class-variance-authority';
import { schemaFieldVariants } from '../constants';

export interface AddressFieldGroupProps extends VariantProps<typeof schemaFieldVariants> {
  form: {
    AppField: React.ComponentType<any>;
    setFieldValue: (field: string, value: string) => void;
  };
  editMode?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
}

export function AddressFieldGroup({
  form,
  editMode = false,
  isLoading = false,
  disabled = false,
  variant,
}: AddressFieldGroupProps) {
  const { AppField } = form;
  const readOnly = !editMode;

  return (
    <>
      <AppField name="street">
        {(field: any) => (
          <StreetAutocomplete
            field={field}
            setFieldValue={form.setFieldValue}
            readOnly={readOnly}
            loading={isLoading}
            disabled={disabled}
            variant={variant}
          />
        )}
      </AppField>

      <AppField name="unit">
        {() => (
          <TextField
            label="Unit"
            readOnly={readOnly}
            loading={isLoading}
            disabled={disabled}
            variant={variant}
          />
        )}
      </AppField>

      <AppField name="city">
        {() => (
          <TextField
            label="City"
            readOnly={readOnly}
            loading={isLoading}
            disabled={disabled}
            variant={variant}
          />
        )}
      </AppField>

      <AppField name="country">
        {() => (
          <CountryWithSubdivisionField
            label="Country"
            subdivisionFieldName="state"
            subdivisionLabel="State"
            readOnly={readOnly}
            loading={isLoading}
            disabled={disabled}
            variant={variant}
          />
        )}
      </AppField>

      <AppField name="postalcode">
        {() => (
          <TextField
            label="Postal Code"
            readOnly={readOnly}
            loading={isLoading}
            disabled={disabled}
            variant={variant}
          />
        )}
      </AppField>
    </>
  );
}

// ---------------------------------------------------------------------------
// StreetAutocomplete — street input with Google Places autocomplete
// ---------------------------------------------------------------------------

interface StreetAutocompleteProps extends VariantProps<typeof schemaFieldVariants> {
  field: any;
  setFieldValue: (field: string, value: string) => void;
  readOnly: boolean;
  loading: boolean;
  disabled: boolean;
}

function StreetAutocomplete({
  field,
  setFieldValue,
  readOnly,
  loading,
  disabled,
  variant,
}: StreetAutocompleteProps) {
  const resolvedVariant = variant ?? 'inline';
  const value = (field.state.value as string) ?? '';
  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;

  const onResolve = (resolved: ResolvedAddress) => {
    setFieldValue('street', resolved.street);
    setFieldValue('city', resolved.city);
    setFieldValue('state', resolved.state);
    setFieldValue('country', resolved.country);
    setFieldValue('postalcode', resolved.postalCode);
  };

  const {
    suggestions,
    isFetching,
    handleSelect: selectPlace,
  } = useStreetAutocomplete({
    input: value,
    onResolve,
    enabled: !readOnly && !disabled,
  });

  const handleSelect = (placeId: string, description: string) => {
    field.handleChange(description); // optimistic — shows description while details load
    selectPlace(placeId);
  };

  return (
    <Field
      orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
      data-invalid={hasError || undefined}
    >
      <FieldLabel htmlFor={field.name}>Street</FieldLabel>
      <FieldContent>
        {loading ? (
          <Skeleton className="h-4 w-full" />
        ) : readOnly ? (
          <p>{value || '—'}</p>
        ) : (
          <AddressAutocomplete
            id={field.name}
            value={value}
            inputValue={value}
            disabled={disabled}
            onInputChange={(val) => field.handleChange(val || undefined)}
            suggestions={suggestions}
            isLoading={isFetching}
            onSelect={handleSelect}
            onBlur={() => {
              const trimmed = (field.state.value as string | undefined)?.trim() || undefined;
              if (trimmed !== field.state.value) field.handleChange(trimmed);
              field.handleBlur();
            }}
          />
        )}
        {hasError && (
          <FieldError errors={errors.map((e: any) => ({ message: String(e.message) }))} />
        )}
      </FieldContent>
    </Field>
  );
}
